import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  LogOut, Shield, Award, Briefcase, FileText, User as UserIcon, 
  Settings, Link as LinkIcon, Plus, Trash2, Edit3, Save, CheckCircle, 
  X, AlertCircle, Menu
} from "lucide-react";
import { authAPI, portfolioAPI, getAssetUrl } from "../services/api";
import { availableIcons } from "../utils/iconHelper";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // Mobile drawer toggle
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Entire portfolio data state
  const [portfolioData, setPortfolioData] = useState(null);

  // Form states
  // Hero
  const [heroBadge, setHeroBadge] = useState("");
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");
  
  // About
  const [aboutHeading, setAboutHeading] = useState("");
  const [aboutDesc, setAboutDesc] = useState("");
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState("");

  // Contact
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactLocation, setContactLocation] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialWhatsapp, setSocialWhatsapp] = useState("");
  const [socialGithub, setSocialGithub] = useState("");
  const [socialLinkedin, setSocialLinkedin] = useState("");

  // Password Settings
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Modals for CRUD operations
  const [skillModal, setSkillModal] = useState({ show: false, mode: "add", data: null });
  const [projectModal, setProjectModal] = useState({ show: false, mode: "add", data: null });
  const [highlightModal, setHighlightModal] = useState({ show: false, mode: "add", data: null });

  // Modal form states
  // Skill form
  const [skillName, setSkillName] = useState("");
  const [skillDesc, setSkillDesc] = useState("");
  const [skillLevel, setSkillLevel] = useState(80);
  const [skillCategory, setSkillCategory] = useState("Game Dev");
  const [skillIcon, setSkillIcon] = useState("FaUnity");

  // Project form
  const [projTitle, setProjTitle] = useState("");
  const [projDesc, setProjDesc] = useState("");
  const [projVideo, setProjVideo] = useState("");
  const [projDemo, setProjDemo] = useState("");
  const [projCode, setProjCode] = useState("");
  const [projTags, setProjTags] = useState("");
  // Dynamic custom action buttons for projects
  const [projButtons, setProjButtons] = useState([]); // [{type, label, url}]

  // Highlight form
  const [hlIcon, setHlIcon] = useState("FaUnity");
  const [hlText, setHlText] = useState("");

  // Validate authentication
  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate("/admin/login");
    } else {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);



  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    const audio = new Audio("https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg");
    audio.play().catch(() => {});
    
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await portfolioAPI.getPortfolioData();
      setPortfolioData(data);

      // Map to form states
      // Hero
      setHeroBadge(data.hero.badge || "");
      setHeroTitle(data.hero.title || "");
      setHeroSubtitle(data.hero.subtitle || "");
      setProfilePreview(getAssetUrl(data.hero.profile_image));
      
      // About
      setAboutHeading(data.about.heading || "");
      setAboutDesc(data.about.description || "");
      setAboutImagePreview(getAssetUrl(data.about.about_image));

      // Contact
      setContactEmail(data.contact.email || "");
      setContactPhone(data.contact.phone || "");
      setContactLocation(data.contact.location || "");
      setSocialFacebook(data.contact.facebook || "");
      setSocialWhatsapp(data.contact.whatsapp || "");
      setSocialGithub(data.contact.github || "");
      setSocialLinkedin(data.contact.linkedin || "");

    } catch (err) {
      showToast(err.message || "Failed to load database data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate("/");
  };

  // Submit Handlers
  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("badge", heroBadge);
      formData.append("title", heroTitle);
      formData.append("subtitle", heroSubtitle);
      if (profileImageFile) formData.append("profileImage", profileImageFile);
      if (resumeFile) formData.append("resumeFile", resumeFile);

      const res = await portfolioAPI.updateHero(formData);
      showToast("Hero section updated successfully.");
      setPortfolioData(prev => ({ ...prev, hero: res.hero }));
      setProfileImageFile(null);
      setResumeFile(null);
    } catch (err) {
      showToast(err.message || "Error updating Hero.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAboutSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("heading", aboutHeading);
      formData.append("description", aboutDesc);
      if (aboutImageFile) formData.append("aboutImage", aboutImageFile);

      const res = await portfolioAPI.updateAbout(formData);
      showToast("About section updated successfully.");
      setPortfolioData(prev => ({ ...prev, about: res.about }));
      setAboutImageFile(null);
    } catch (err) {
      showToast(err.message || "Error updating About.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const contactData = {
        email: contactEmail,
        phone: contactPhone,
        location: contactLocation,
        facebook: socialFacebook,
        whatsapp: socialWhatsapp,
        github: socialGithub,
        linkedin: socialLinkedin
      };
      const res = await portfolioAPI.updateContact(contactData);
      showToast("Contact and social links updated.");
      setPortfolioData(prev => ({ ...prev, contact: res.contact }));
    } catch (err) {
      showToast(err.message || "Error updating contact.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast("New passwords do not match.", "error");
      return;
    }
    setSaving(true);
    try {
      await authAPI.updatePassword(currentPassword, newPassword);
      showToast("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      showToast(err.message || "Error changing password.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Highlights Operations
  const openAddHighlight = () => {
    setHlIcon("FaUnity");
    setHlText("");
    setHighlightModal({ show: true, mode: "add", data: null });
  };

  const openEditHighlight = (hl) => {
    setHlIcon(hl.icon_name);
    setHlText(hl.text);
    setHighlightModal({ show: true, mode: "edit", data: hl });
  };

  const handleHighlightSubmit = async (e) => {
    e.preventDefault();
    try {
      const hlData = { icon_name: hlIcon, text: hlText };
      if (highlightModal.mode === "add") {
        const res = await portfolioAPI.addHighlight(hlData);
        setPortfolioData(prev => ({ ...prev, highlights: [...prev.highlights, res.highlight] }));
        showToast("Highlight added successfully.");
      } else {
        const id = highlightModal.data.id;
        const res = await portfolioAPI.updateHighlight(id, hlData);
        setPortfolioData(prev => ({
          ...prev,
          highlights: prev.highlights.map(item => item.id === id ? res.highlight : item)
        }));
        showToast("Highlight updated successfully.");
      }
      setHighlightModal({ show: false, mode: "add", data: null });
    } catch (err) {
      showToast(err.message || "Operation failed.", "error");
    }
  };

  const deleteHighlight = async (id) => {
    if (!window.confirm("Are you sure you want to delete this highlight?")) return;
    try {
      await portfolioAPI.deleteHighlight(id);
      setPortfolioData(prev => ({
        ...prev,
        highlights: prev.highlights.filter(item => item.id !== id)
      }));
      showToast("Highlight deleted.");
    } catch (err) {
      showToast(err.message || "Failed to delete highlight.", "error");
    }
  };

  // Skills Operations
  const openAddSkill = () => {
    setSkillName("");
    setSkillDesc("");
    setSkillLevel(80);
    setSkillCategory("Game Dev");
    setSkillIcon("FaUnity");
    setSkillModal({ show: true, mode: "add", data: null });
  };

  const openEditSkill = (s) => {
    setSkillName(s.name);
    setSkillDesc(s.description || "");
    setSkillLevel(s.level);
    setSkillCategory(s.category);
    setSkillIcon(s.icon_name);
    setSkillModal({ show: true, mode: "edit", data: s });
  };

  const handleSkillSubmit = async (e) => {
    e.preventDefault();
    try {
      const skillData = {
        name: skillName,
        description: skillDesc,
        level: parseInt(skillLevel),
        category: skillCategory,
        icon_name: skillIcon
      };
      if (skillModal.mode === "add") {
        const res = await portfolioAPI.addSkill(skillData);
        setPortfolioData(prev => ({ ...prev, skills: [...prev.skills, res.skill] }));
        showToast("Skill added successfully.");
      } else {
        const id = skillModal.data.id;
        const res = await portfolioAPI.updateSkill(id, skillData);
        setPortfolioData(prev => ({
          ...prev,
          skills: prev.skills.map(item => item.id === id ? res.skill : item)
        }));
        showToast("Skill updated successfully.");
      }
      setSkillModal({ show: false, mode: "add", data: null });
    } catch (err) {
      showToast(err.message || "Operation failed.", "error");
    }
  };

  const deleteSkill = async (id) => {
    if (!window.confirm("Are you sure you want to delete this skill?")) return;
    try {
      await portfolioAPI.deleteSkill(id);
      setPortfolioData(prev => ({
        ...prev,
        skills: prev.skills.filter(item => item.id !== id)
      }));
      showToast("Skill deleted.");
    } catch (err) {
      showToast(err.message || "Failed to delete skill.", "error");
    }
  };

  // Projects Operations
  const openAddProject = () => {
    setProjTitle("");
    setProjDesc("");
    setProjVideo("");
    setProjDemo("");
    setProjCode("");
    setProjTags("");
    setProjButtons([]);
    setProjectModal({ show: true, mode: "add", data: null });
  };

  const openEditProject = (p) => {
    setProjTitle(p.title);
    setProjDesc(p.description);
    setProjVideo(p.video_url || "");
    setProjDemo(p.demo_url || "");
    setProjCode(p.code_url || "");
    setProjTags(p.tags || "");
    // Parse stored buttons JSON or build from legacy demo/code fields
    try {
      const parsed = p.buttons ? JSON.parse(p.buttons) : [];
      setProjButtons(Array.isArray(parsed) ? parsed : []);
    } catch { setProjButtons([]); }
    setProjectModal({ show: true, mode: "edit", data: p });
  };

  // Button helpers
  const addProjButton = () => {
    setProjButtons(prev => [...prev, { type: "website", label: "", url: "" }]);
  };
  const removeProjButton = (idx) => {
    setProjButtons(prev => prev.filter((_, i) => i !== idx));
  };
  const updateProjButton = (idx, field, value) => {
    setProjButtons(prev => prev.map((btn, i) => i === idx ? { ...btn, [field]: value } : btn));
  };

  const BUTTON_TYPES = [
    { value: "website",   label: "Website / Live Demo",  icon: "🌐" },
    { value: "github",    label: "GitHub / Source Code",  icon: "🐙" },
    { value: "playstore", label: "Play Store (Android)",  icon: "▶️" },
    { value: "appstore",  label: "App Store (iOS/macOS)", icon: "🍎" },
    { value: "custom",    label: "Custom Button",          icon: "🔗" },
  ];

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        title: projTitle,
        description: projDesc,
        video_url: projVideo,
        demo_url: projDemo,
        code_url: projCode,
        tags: projTags,
        buttons: JSON.stringify(projButtons.filter(b => b.url && b.label))
      };
      if (projectModal.mode === "add") {
        const res = await portfolioAPI.addProject(projectData);
        setPortfolioData(prev => ({ ...prev, projects: [...prev.projects, res.project] }));
        showToast("Project added successfully.");
      } else {
        const id = projectModal.data.id;
        const res = await portfolioAPI.updateProject(id, projectData);
        setPortfolioData(prev => ({
          ...prev,
          projects: prev.projects.map(item => item.id === id ? res.project : item)
        }));
        showToast("Project updated successfully.");
      }
      setProjectModal({ show: false, mode: "add", data: null });
    } catch (err) {
      showToast(err.message || "Operation failed.", "error");
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await portfolioAPI.deleteProject(id);
      setPortfolioData(prev => ({
        ...prev,
        projects: prev.projects.filter(item => item.id !== id)
      }));
      showToast("Project deleted.");
    } catch (err) {
      showToast(err.message || "Failed to delete project.", "error");
    }
  };

  // Image handlers
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfilePreview(URL.createObjectURL(file));
    }
  };

  const handleAboutImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAboutImageFile(file);
      setAboutImagePreview(URL.createObjectURL(file));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center">
        <div className="relative w-16 h-16">
          <div className="w-16 h-16 rounded-full border-4 border-indigo-200 dark:border-indigo-900 border-t-indigo-500 animate-spin" />
        </div>
        <p className="mt-4 font-[Poppins] text-indigo-500 dark:text-indigo-400 font-medium animate-pulse">Loading Admin Dashboard...</p>
      </div>
    );
  }

  const menuTabs = [
    { id: "hero", label: "Hero Section", icon: <UserIcon size={18} /> },
    { id: "about", label: "About & Highlights", icon: <Award size={18} /> },
    { id: "skills", label: "Skills Catalog", icon: <Briefcase size={18} /> },
    { id: "projects", label: "Projects Manager", icon: <FileText size={18} /> },
    { id: "contact", label: "Contact Details", icon: <LinkIcon size={18} /> },
    { id: "settings", label: "Security Settings", icon: <Settings size={18} /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 flex flex-col font-[Poppins] transition-colors duration-500">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 20, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border backdrop-blur-xl ${
              toast.type === "success" 
                ? "bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400" 
                : "bg-red-500/10 border-red-500/30 text-red-500 dark:text-red-400"
            }`}
          >
            {toast.type === "success" ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span className="text-sm font-medium">{toast.message}</span>
            <div className="absolute bottom-0 left-0 h-1 bg-current w-full animate-toastProgress" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header (Top Bar) */}
      <header className="bg-white/90 dark:bg-slate-900/80 border-b border-slate-200/80 dark:border-slate-800/80 backdrop-blur-lg px-6 py-4 flex items-center justify-between sticky top-0 z-30 transition-all shadow-sm">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsDrawerOpen(true)}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg md:hidden transition"
          >
            <Menu size={22} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 dark:bg-indigo-500 text-white p-2 rounded-xl shadow-[0_4px_12px_rgba(99,102,241,0.2)] flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-md font-extrabold font-[Poppins] tracking-wide text-slate-900 dark:text-white">
                Portfolio Console
              </h1>
              <span className="text-[9px] font-extrabold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded-md uppercase tracking-wider font-[Inter] border border-indigo-100 dark:border-indigo-900/30">
                v2.0
              </span>
            </div>
          </div>
        </div>

        {/* Database Status and Navigation utilities */}
        <div className="flex items-center gap-4">
          {/* Pulsing Status indicator */}
          <div className="hidden lg:flex items-center gap-2 text-[11px] font-semibold text-slate-600 dark:text-slate-400 bg-slate-100/50 dark:bg-slate-900/50 px-3 py-1.5 rounded-xl border border-slate-200/30 dark:border-slate-800/40 font-[Inter]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span>Database Online</span>
          </div>


          
          <button
            onClick={() => navigate("/")}
            className="px-3.5 py-2 border border-slate-300 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 hover:bg-slate-100/30 dark:hover:bg-slate-900/30 text-slate-700 dark:text-white rounded-xl text-xs font-semibold transition"
          >
            Preview Site
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <div className="fixed inset-0 z-40 md:hidden flex">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDrawerOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            />
            {/* Drawer */}
            <motion.div 
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="relative w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 py-6 px-4 flex flex-col justify-between z-10 transition-colors"
            >
              <div className="space-y-6">
                {/* Mobile Drawer Header */}
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
                  <div className="flex items-center gap-2">
                    <Shield className="text-indigo-500 w-5 h-5" />
                    <span className="font-bold text-slate-900 dark:text-white text-sm">Navigation Menu</span>
                  </div>
                  <button onClick={() => setIsDrawerOpen(false)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800/60 p-1 rounded-lg">
                    <X size={16} />
                  </button>
                </div>

                {/* Mobile Profile Module */}
                <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-950/40 p-3 rounded-2xl border border-slate-200/60 dark:border-slate-800/60">
                  <img 
                    src={profilePreview || "/placeholder.jpg"} 
                    alt="Admin Mobile Preview" 
                    onError={(e) => { e.target.onerror = null; e.target.src = "/Profile.jpeg"; }}
                    className="w-10 h-10 rounded-full object-cover border border-indigo-200 dark:border-indigo-900/30"
                  />
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 dark:text-white">Abdul Haseeb</h4>
                    <p className="text-[10px] text-slate-500 font-medium">Administrator</p>
                  </div>
                </div>

                {/* Mobile Navigation links */}
                <nav className="space-y-1">
                  {menuTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setIsDrawerOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition ${
                        activeTab === tab.id
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/10"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/40 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Mobile Drawer Logout at Bottom */}
              <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-red-500/5 hover:bg-red-600 border border-red-200 dark:border-red-500/10 text-red-500 hover:text-white rounded-xl text-xs font-bold transition duration-300"
                >
                  <LogOut size={14} /> Log Out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Main Layout */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Desktop Sidebar (Solid Full-height Pane) */}
        <aside className="hidden md:flex w-64 bg-slate-50 dark:bg-slate-950 border-r border-slate-200/80 dark:border-slate-900/80 py-6 px-4 shrink-0 flex-col justify-between min-h-[calc(100vh-73px)] transition-colors">
          <div>
            {/* Administrator Profile Card */}
            <div className="border-b border-slate-200/80 dark:border-slate-800/60 pb-5 mb-5 flex items-center gap-3 px-1.5">
              <div className="relative shrink-0">
                <img 
                  src={profilePreview || "/placeholder.jpg"} 
                  alt="Administrator Profile" 
                  onError={(e) => { e.target.onerror = null; e.target.src = "/Profile.jpeg"; }}
                  className="w-11 h-11 rounded-full object-cover border border-slate-200 dark:border-indigo-500/20"
                />
                {/* Active Pulsing Indicator Badge */}
                <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-slate-50 dark:ring-slate-950 bg-emerald-500" />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white tracking-wide">
                  Abdul Haseeb
                </h3>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20 px-1 rounded uppercase tracking-wider font-[Inter]">
                    Admin
                  </span>
                </div>
              </div>
            </div>

            {/* Sidebar Navigation list */}
            <nav className="space-y-1.5 relative">
              {menuTabs.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition duration-300 ${
                      isActive
                        ? "bg-indigo-50/80 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-semibold"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/40 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    {/* Active Left Indicator Bar */}
                    {isActive && (
                      <div className="absolute left-0 top-2.5 bottom-2.5 w-[3.5px] rounded-r-md bg-indigo-600 dark:bg-indigo-500" />
                    )}
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Logout Button */}
          <div className="border-t border-slate-200/80 dark:border-slate-800/60 pt-4 mt-6">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-4 py-3 bg-red-500/5 hover:bg-red-600 border border-red-200/40 dark:border-red-500/10 hover:border-red-500 text-red-500 dark:text-red-400 hover:text-white rounded-xl text-xs font-semibold transition duration-300"
            >
              <LogOut size={14} /> Log Out Session
            </button>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 p-5 sm:p-8 bg-white dark:bg-slate-900 overflow-y-auto transition-colors">
          {/* TAB: HERO */}
          {activeTab === "hero" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl space-y-6">
              <div className="border-b border-slate-200 dark:border-slate-800 pb-4 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Hero Section</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Edit the greeting badge, main title words, description, CV, and profile picture.</p>
              </div>

              <form onSubmit={handleHeroSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Badge */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">Greeting Badge</label>
                    <input 
                      type="text" 
                      value={heroBadge} 
                      onChange={e => setHeroBadge(e.target.value)} 
                      placeholder="e.g. 🎮 Game Developer" 
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>

                  {/* Title */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">Main Title</label>
                    <input 
                      type="text" 
                      value={heroTitle} 
                      onChange={e => setHeroTitle(e.target.value)} 
                      placeholder="e.g. Hi, I’m Abdul Haseeb" 
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Subtitle */}
                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">Subtitle Description</label>
                  <textarea 
                    rows={4} 
                    value={heroSubtitle} 
                    onChange={e => setHeroSubtitle(e.target.value)} 
                    placeholder="Enter hero paragraph..." 
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none font-[Inter] leading-relaxed transition-all"
                  />
                </div>

                {/* Files Upload Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Image */}
                  <div className="bg-slate-50/50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex items-center gap-4">
                    <img 
                      src={profilePreview || "/placeholder.jpg"} 
                      alt="Profile Preview" 
                      onError={(e) => { e.target.onerror = null; e.target.src = "/Profile.jpeg"; }}
                      className="w-20 h-20 rounded-full object-cover border border-slate-200 dark:border-indigo-500/20 shrink-0"
                    />
                    <div className="space-y-1">
                      <label className="text-xs text-slate-500 dark:text-slate-300 font-semibold block">Profile Image</label>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleProfileImageChange}
                        className="text-xs text-slate-500 dark:text-slate-400 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-600/10 file:text-indigo-600 dark:file:text-indigo-400 hover:file:bg-indigo-600/20"
                      />
                    </div>
                  </div>

                  {/* Resume PDF */}
                  <div className="bg-slate-50/50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex flex-col justify-center gap-2">
                    <label className="text-xs text-slate-500 dark:text-slate-300 font-semibold block">Resume / CV Document (PDF)</label>
                    <input 
                      type="file" 
                      accept=".pdf" 
                      onChange={e => setResumeFile(e.target.files[0])}
                      className="text-xs text-slate-500 dark:text-slate-400 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-600/10 file:text-indigo-600 dark:file:text-indigo-400 hover:file:bg-indigo-600/20"
                    />
                    {portfolioData.hero.resume_url && (
                      <p className="text-[10px] text-slate-500 font-[Inter] truncate">Current: {portfolioData.hero.resume_url}</p>
                    )}
                  </div>
                </div>

                <motion.button 
                  whileHover={{ scale: 1.01 }} 
                  whileTap={{ scale: 0.99 }} 
                  type="submit" 
                  disabled={saving} 
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/20 hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {saving ? "Saving..." : <><Save size={16} /> Save Changes</>}
                </motion.button>
              </form>
            </motion.div>
          )}

          {/* TAB: ABOUT */}
          {activeTab === "about" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl space-y-8">
              <div className="border-b border-slate-200 dark:border-slate-800 pb-4 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">About Me & Highlights</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Edit the about section content, photo, and manage the grid highlights checklist.</p>
              </div>

              {/* Main About Form */}
              <form onSubmit={handleAboutSubmit} className="space-y-6 bg-slate-50/50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/80 rounded-3xl p-6">
                <h3 className="text-base font-bold text-indigo-600 dark:text-indigo-400 mb-2 border-b border-slate-200 dark:border-slate-800 pb-2">Content Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Image Preview */}
                  <div className="flex flex-col items-center justify-center bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 gap-3">
                    <img 
                      src={aboutImagePreview || "/placeholder.jpg"} 
                      alt="About Preview" 
                      onError={(e) => { e.target.onerror = null; e.target.src = "/about.png"; }}
                      className="w-40 h-40 rounded-2xl object-cover border border-slate-200 dark:border-slate-800 shadow-md"
                    />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleAboutImageChange}
                      className="text-[10px] text-slate-500 dark:text-slate-400 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-[10px] file:bg-indigo-600/10 file:text-indigo-600 dark:file:text-indigo-400"
                    />
                  </div>

                  {/* Heading & Description */}
                  <div className="md:col-span-2 space-y-4">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Heading Title</label>
                      <input 
                        type="text" 
                        value={aboutHeading} 
                        onChange={e => setAboutHeading(e.target.value)} 
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">About Description</label>
                      <textarea 
                        rows={5} 
                        value={aboutDesc} 
                        onChange={e => setAboutDesc(e.target.value)} 
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none font-[Inter] leading-relaxed transition-all"
                      />
                    </div>
                  </div>
                </div>

                <motion.button 
                  whileHover={{ scale: 1.01 }} 
                  whileTap={{ scale: 0.99 }} 
                  type="submit" 
                  disabled={saving} 
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/20 hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {saving ? "Saving..." : <><Save size={16} /> Save About Content</>}
                </motion.button>
              </form>

              {/* Highlights List Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                  <h3 className="text-base font-bold text-indigo-600 dark:text-indigo-400">Highlights Checklist</h3>
                  <button 
                    onClick={openAddHighlight}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition"
                  >
                    <Plus size={14} /> Add Highlight
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {portfolioData.highlights.map(hl => (
                    <div 
                      key={hl.id} 
                      className="flex items-center justify-between p-4 bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/60 rounded-2xl hover:border-slate-300 dark:hover:border-slate-800 transition"
                    >
                      <div className="flex flex-col gap-1.5">
                        <span className="self-start text-[9px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md border border-indigo-200 dark:border-indigo-900/30 font-[Inter]">
                          {hl.icon_name}
                        </span>
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{hl.text}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        <button 
                          onClick={() => openEditHighlight(hl)}
                          className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button 
                          onClick={() => deleteHighlight(hl.id)}
                          className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB: SKILLS */}
          {activeTab === "skills" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Skills Catalog</h2>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Add, edit, or delete professional developer skills displayed on the grid.</p>
                </div>
                <button 
                  onClick={openAddSkill}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition"
                >
                  <Plus size={16} /> Add Skill
                </button>
              </div>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolioData.skills.map(s => (
                  <div 
                    key={s.id} 
                    className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/60 rounded-3xl p-5 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-800 hover:shadow-lg transition duration-300"
                  >
                    <div>
                      {/* Top Header */}
                      <div className="flex items-start justify-between mb-3">
                        <span className="px-2 py-0.5 text-[9px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900/30 rounded-md uppercase font-[Inter]">
                          {s.category}
                        </span>
                        <span className="text-[10px] text-slate-500 font-bold bg-slate-100 dark:bg-slate-800/60 px-2 py-0.5 rounded-md">
                          {s.icon_name}
                        </span>
                      </div>

                      {/* Title & Level */}
                      <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-1.5">{s.name}</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-[Inter] leading-relaxed mb-4 line-clamp-2 h-8">{s.description || "—"}</p>
                      
                      {/* Progress line */}
                      <div className="space-y-1 mb-2">
                        <div className="flex justify-between text-[11px] font-semibold text-slate-500 font-[Inter]">
                          <span>Proficiency</span>
                          <span>{s.level}%</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 h-2 rounded-full overflow-hidden">
                          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-full rounded-full" style={{ width: `${s.level}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Actions footer */}
                    <div className="flex gap-3 border-t border-slate-200 dark:border-slate-800/60 pt-4 mt-4">
                      <button 
                        onClick={() => openEditSkill(s)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-850 text-xs font-semibold rounded-xl text-indigo-600 dark:text-indigo-400 border border-slate-200 dark:border-slate-800 hover:text-indigo-700 dark:hover:text-white transition"
                      >
                        <Edit3 size={13} /> Edit
                      </button>
                      <button 
                        onClick={() => deleteSkill(s.id)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-50 dark:bg-red-950/10 border border-red-200 dark:border-red-900/20 hover:bg-red-600 hover:text-white text-xs font-semibold rounded-xl text-red-500 transition"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* TAB: PROJECTS */}
          {activeTab === "projects" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Projects Manager</h2>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Edit, add, or delete items inside the interactive project portfolio catalog.</p>
                </div>
                <button 
                  onClick={openAddProject}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition"
                >
                  <Plus size={16} /> Add Project
                </button>
              </div>

              {/* Projects Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {portfolioData.projects.map(p => (
                  <div key={p.id} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/60 rounded-3xl p-5 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-800 hover:shadow-lg transition duration-300">
                    <div>
                      {/* Video iframe preview or thumbnail */}
                      {p.video_url && (
                        <div className="w-full h-44 sm:h-52 bg-black rounded-2xl overflow-hidden mb-4 border border-slate-200 dark:border-slate-800">
                          <iframe 
                            src={p.video_url} 
                            title={p.title} 
                            className="w-full h-full opacity-80" 
                            allowFullScreen
                          />
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg text-slate-900 dark:text-white">{p.title}</h3>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => openEditProject(p)}
                            className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-800"
                          >
                            <Edit3 size={13} />
                          </button>
                          <button 
                            onClick={() => deleteProject(p.id)}
                            className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-800"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      <p className="text-slate-500 dark:text-slate-400 text-xs font-[Inter] leading-relaxed mb-4 line-clamp-3">{p.description}</p>
                      
                      {/* Tags - optional */}
                      {p.tags && p.tags.trim() && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {p.tags.split(",").filter(t => t.trim()).map((t, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-[9px] font-bold bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/20 text-indigo-650 dark:text-indigo-400 rounded-md uppercase font-[Inter]">
                              {t.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Dynamic Action Buttons */}
                    {(() => {
                      const ICONS_MAP = { playstore: "▶️", appstore: "🍎", github: "🐙", website: "🌐", custom: "🔗" };
                      let btns = [];
                      try { btns = p.buttons ? JSON.parse(p.buttons) : []; } catch {}
                      // Fallback to legacy demo/code
                      if (!btns.length) {
                        if (p.demo_url && p.demo_url !== "#") btns.push({ type: "website", label: "Live Demo", url: p.demo_url });
                        if (p.code_url && p.code_url !== "#") btns.push({ type: "github", label: "Source Code", url: p.code_url });
                      }
                      return btns.length > 0 ? (
                        <div className="flex flex-wrap gap-2 border-t border-slate-200 dark:border-slate-800/60 pt-4 mt-2">
                          {btns.map((btn, bi) => (
                            <a key={bi} href={btn.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-xs font-semibold rounded-xl text-slate-700 dark:text-white transition">
                              <span>{ICONS_MAP[btn.type] || "🔗"}</span> {btn.label}
                            </a>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* TAB: CONTACT & SOCIALS */}
          {activeTab === "contact" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl space-y-6">
              <div className="border-b border-slate-200 dark:border-slate-800 pb-4 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Contact Details</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Edit contact details and social media profile URLs rendered on page and footer.</p>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50/50 dark:bg-slate-900/10 border border-slate-200 dark:border-slate-800/80 rounded-3xl p-6">
                  <div className="md:col-span-3 border-b border-slate-200 dark:border-slate-800 pb-2">
                    <h3 className="text-sm font-bold text-indigo-600 dark:text-indigo-400">Direct Contact Coordinates</h3>
                  </div>
                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Email Address</label>
                    <input 
                      type="email" 
                      required
                      value={contactEmail} 
                      onChange={e => setContactEmail(e.target.value)} 
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Phone Number</label>
                    <input 
                      type="text" 
                      required
                      value={contactPhone} 
                      onChange={e => setContactPhone(e.target.value)} 
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Location Town/City</label>
                    <input 
                      type="text" 
                      required
                      value={contactLocation} 
                      onChange={e => setContactLocation(e.target.value)} 
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 dark:bg-slate-900/10 border border-slate-200 dark:border-slate-800/80 rounded-3xl p-6">
                  <div className="md:col-span-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                    <h3 className="text-sm font-bold text-indigo-600 dark:text-indigo-400">Social Profile URLs</h3>
                  </div>

                  {/* WhatsApp */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">WhatsApp API Link</label>
                    <input 
                      type="url" 
                      value={socialWhatsapp} 
                      onChange={e => setSocialWhatsapp(e.target.value)} 
                      placeholder="https://wa.me/..."
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>

                  {/* Github */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">GitHub Profile Link</label>
                    <input 
                      type="url" 
                      value={socialGithub} 
                      onChange={e => setSocialGithub(e.target.value)} 
                      placeholder="https://github.com/..."
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>

                  {/* LinkedIn */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">LinkedIn Profile Link</label>
                    <input 
                      type="url" 
                      value={socialLinkedin} 
                      onChange={e => setSocialLinkedin(e.target.value)} 
                      placeholder="https://linkedin.com/in/..."
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>

                  {/* Facebook */}
                  <div className="space-y-2">
                    <label className="text-xs text-slate-505 dark:text-slate-400 font-semibold block">Facebook Profile Link</label>
                    <input 
                      type="url" 
                      value={socialFacebook} 
                      onChange={e => setSocialFacebook(e.target.value)} 
                      placeholder="https://facebook.com/..."
                      className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                </div>

                <motion.button 
                  whileHover={{ scale: 1.01 }} 
                  whileTap={{ scale: 0.99 }} 
                  type="submit" 
                  disabled={saving} 
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/20 hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {saving ? "Saving..." : <><Save size={16} /> Save Contact Info</>}
                </motion.button>
              </form>
            </motion.div>
          )}

          {/* TAB: SETTINGS */}
          {activeTab === "settings" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-md space-y-6">
              <div className="border-b border-slate-205 dark:border-slate-800 pb-4 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Account Security</h2>
                <p className="text-xs sm:text-sm text-slate-500">Update current credentials for admin security.</p>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">Current Password</label>
                  <input 
                    type="password" 
                    required
                    value={currentPassword} 
                    onChange={e => setCurrentPassword(e.target.value)} 
                    placeholder="Current password" 
                    className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">New Password</label>
                  <input 
                    type="password" 
                    required
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    placeholder="New password" 
                    className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider block">Confirm New Password</label>
                  <input 
                    type="password" 
                    required
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="Confirm new password" 
                    className="w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none transition-all"
                  />
                </div>

                <motion.button 
                  whileHover={{ scale: 1.01 }} 
                  whileTap={{ scale: 0.99 }} 
                  type="submit" 
                  disabled={saving} 
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/20 hover:bg-indigo-700 disabled:opacity-50 transition mt-6"
                >
                  {saving ? "Saving..." : <><Save size={16} /> Update Password</>}
                </motion.button>
              </form>
            </motion.div>
          )}
        </main>
      </div>

      {/* ======================================================== */}
      {/* DIALOG MODALS FOR CRUD */}
      {/* ======================================================== */}

      {/* Modal: Highlight */}
      <AnimatePresence>
        {highlightModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 dark:bg-slate-950/80 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.95, y: 15, opacity: 0 }} 
              animate={{ scale: 1, y: 0, opacity: 1 }} 
              exit={{ scale: 0.95, y: 15, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-md rounded-3xl p-6 shadow-2xl relative text-slate-900 dark:text-white"
            >
              <button 
                onClick={() => setHighlightModal({ show: false, mode: "add", data: null })}
                className="absolute top-4 right-4 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 transition"
              >
                <X size={16} />
              </button>

              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
                {highlightModal.mode === "add" ? "Create New Highlight" : "Edit Highlight"}
              </h3>

              <form onSubmit={handleHighlightSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Icon Template</label>
                  <select 
                    value={hlIcon} 
                    onChange={e => setHlIcon(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Highlight Title Text</label>
                  <input 
                    type="text" 
                    required
                    value={hlText} 
                    onChange={e => setHlText(e.target.value)} 
                    placeholder="e.g. Unity (2D & 3D Game Dev)"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-lg transition mt-4"
                >
                  {highlightModal.mode === "add" ? "Create Highlight" : "Save Changes"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal: Skill */}
      <AnimatePresence>
        {skillModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 dark:bg-slate-950/80 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.95, y: 15, opacity: 0 }} 
              animate={{ scale: 1, y: 0, opacity: 1 }} 
              exit={{ scale: 0.95, y: 15, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-md rounded-3xl p-6 shadow-2xl relative text-slate-900 dark:text-white"
            >
              <button 
                onClick={() => setSkillModal({ show: false, mode: "add", data: null })}
                className="absolute top-4 right-4 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 transition"
              >
                <X size={16} />
              </button>

              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
                {skillModal.mode === "add" ? "Create Skill" : "Edit Skill Info"}
              </h3>

              <form onSubmit={handleSkillSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Skill Title</label>
                    <input 
                      type="text" 
                      required
                      value={skillName} 
                      onChange={e => setSkillName(e.target.value)} 
                      placeholder="e.g. Unity Engine"
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Icon</label>
                    <select 
                      value={skillIcon} 
                      onChange={e => setSkillIcon(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    >
                      {availableIcons.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Category</label>
                    <select 
                      value={skillCategory} 
                      onChange={e => setSkillCategory(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    >
                      <option value="Game Dev">Game Dev</option>
                      <option value="Programming">Programming</option>
                      <option value="Tools">Tools</option>
                      <option value="Cloud">Cloud</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Proficiency (0-100%)</label>
                    <input 
                      type="number" 
                      required
                      min={0}
                      max={100}
                      value={skillLevel} 
                      onChange={e => setSkillLevel(e.target.value)} 
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 font-semibold block">Short description</label>
                  <input 
                    type="text" 
                    value={skillDesc} 
                    onChange={e => setSkillDesc(e.target.value)} 
                    placeholder="e.g. Gameplay programming"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 focus:border-indigo-500 rounded-xl p-3 text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-lg transition mt-4"
                >
                  {skillModal.mode === "add" ? "Create Skill" : "Save Changes"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal: Project */}
      <AnimatePresence>
        {projectModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.95, y: 15, opacity: 0 }} 
              animate={{ scale: 1, y: 0, opacity: 1 }} 
              exit={{ scale: 0.95, y: 15, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-2xl rounded-3xl shadow-2xl relative text-slate-900 dark:text-white overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/40">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                    <FileText size={16} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                      {projectModal.mode === "add" ? "Create New Project" : "Edit Project Details"}
                    </h3>
                    <p className="text-[10px] text-slate-500 font-[Inter]">All fields marked * are required</p>
                  </div>
                </div>
                <button 
                  onClick={() => setProjectModal({ show: false, mode: "add", data: null })}
                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 transition"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto max-h-[75vh] p-6">
                <form onSubmit={handleProjectSubmit} className="space-y-5">

                  {/* Row 1: Title + Tags */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block">Project Title *</label>
                      <input 
                        type="text" 
                        required
                        value={projTitle} 
                        onChange={e => setProjTitle(e.target.value)} 
                        placeholder="e.g. My Awesome App"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block">
                        Tech Tags <span className="text-slate-400 font-normal normal-case">(optional, comma-separated)</span>
                      </label>
                      <input 
                        type="text" 
                        value={projTags} 
                        onChange={e => setProjTags(e.target.value)} 
                        placeholder="React, Node.js, Unity..."
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 dark:text-white outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block">Project Description *</label>
                    <textarea 
                      rows={3}
                      required
                      value={projDesc} 
                      onChange={e => setProjDesc(e.target.value)} 
                      placeholder="Describe what this project does, its purpose and key features..."
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 dark:text-white outline-none font-[Inter] leading-relaxed transition-all resize-none"
                    />
                  </div>

                  {/* YouTube Embed */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block">
                      YouTube Embed URL <span className="text-slate-400 font-normal normal-case">(optional)</span>
                    </label>
                    <input 
                      type="url" 
                      value={projVideo} 
                      onChange={e => setProjVideo(e.target.value)} 
                      placeholder="https://www.youtube.com/embed/VIDEO_ID"
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 dark:text-white outline-none transition-all"
                    />
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3 py-1">
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action Buttons</span>
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                  </div>

                  {/* Dynamic Buttons Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-[Inter]">Add links that appear as buttons on the project card. Leave empty to show no buttons.</p>
                      <button 
                        type="button"
                        onClick={addProjButton}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition shrink-0 ml-3"
                      >
                        <Plus size={12} /> Add Button
                      </button>
                    </div>

                    {projButtons.length === 0 && (
                      <div className="text-center py-5 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                        <p className="text-xs text-slate-400 font-[Inter]">No buttons added yet</p>
                        <p className="text-[10px] text-slate-300 dark:text-slate-600 mt-0.5">Click "Add Button" to add Play Store, GitHub, Website links etc.</p>
                      </div>
                    )}

                    {projButtons.map((btn, idx) => (
                      <motion.div 
                        key={idx}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start gap-2.5 p-3.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-2xl"
                      >
                        {/* Button Type */}
                        <div className="space-y-1 w-36 shrink-0">
                          <label className="text-[10px] text-slate-400 font-semibold block">Type</label>
                          <select
                            value={btn.type}
                            onChange={e => updateProjButton(idx, "type", e.target.value)}
                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:border-indigo-500"
                          >
                            {BUTTON_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Button Label */}
                        <div className="space-y-1 flex-1">
                          <label className="text-[10px] text-slate-400 font-semibold block">Button Label</label>
                          <input
                            type="text"
                            value={btn.label}
                            onChange={e => updateProjButton(idx, "label", e.target.value)}
                            placeholder={btn.type === "playstore" ? "Play Store" : btn.type === "appstore" ? "App Store" : btn.type === "github" ? "Source Code" : "Live Demo"}
                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:border-indigo-500"
                          />
                        </div>

                        {/* Button URL */}
                        <div className="space-y-1 flex-1">
                          <label className="text-[10px] text-slate-400 font-semibold block">URL / Link</label>
                          <input
                            type="text"
                            value={btn.url}
                            onChange={e => updateProjButton(idx, "url", e.target.value)}
                            placeholder={btn.type === "playstore" ? "https://play.google.com/..." : btn.type === "appstore" ? "https://apps.apple.com/..." : "https://..."}
                            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:border-indigo-500"
                          />
                        </div>

                        {/* Remove */}
                        <button 
                          type="button" 
                          onClick={() => removeProjButton(idx)}
                          className="mt-5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition shrink-0"
                        >
                          <Trash2 size={13} />
                        </button>
                      </motion.div>
                    ))}

                    {/* Quick presets */}
                    {projButtons.length < 4 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        <span className="text-[10px] text-slate-400 font-semibold self-center">Quick Add:</span>
                        {[
                          { type: "playstore", label: "▶️ Play Store" },
                          { type: "appstore",  label: "🍎 App Store" },
                          { type: "github",    label: "🐙 GitHub" },
                          { type: "website",   label: "🌐 Website" },
                        ].map(preset => (
                          <button
                            key={preset.type}
                            type="button"
                            onClick={() => setProjButtons(prev => [...prev, { type: preset.type, label: preset.label.split(" ").slice(1).join(" "), url: "" }])}
                            className="px-2.5 py-1 text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700 transition"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="pt-2">
                    <button 
                      type="submit" 
                      className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/20 transition text-sm"
                    >
                      {projectModal.mode === "add" ? "✨ Create Project" : "💾 Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
